import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  Avatar,
  CircularProgress,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import { ChatMessage } from '../../types/chat';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import throttle from 'lodash/throttle';

interface ClientChatProps {
  messages: ChatMessage[];
  isLoading: boolean;
  onSendMessage: (msg: string) => Promise<{ success: boolean; error?: string }>;
  onMarkAsRead : () => Promise<void>;
  onSendTyping?: () => void;
  clientName   : string;
  agentName    : string;
}

const ClientChat = ({ 
  messages, 
  isLoading, 
  onSendMessage, 
  onMarkAsRead,
  onSendTyping,
  clientName,
}: ClientChatProps) => {
  const theme = useTheme();
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  /*──────────────── auto-scroll support ────────────────*/
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = (behavior: ScrollBehavior = 'auto') =>
    messagesEndRef.current?.scrollIntoView({ behavior });

  /* jump after history load */
  useEffect(() => {
    if (!isLoading) scrollToBottom('auto');
  }, [isLoading]);

  /* smooth scroll on every new message */
  useEffect(() => {
    if (!isLoading) scrollToBottom('smooth');
  }, [messages, isLoading]);
  /*─────────────────────────────────────────────────────*/

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    try {
      const result = await onSendMessage(newMessage.trim());
      if (result.success) {
        setNewMessage('');
      } else {
        console.error('Failed to send message:', result.error);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown';
    }
  };

  // Throttled typing notifier
  const throttledTyping = useRef(
    throttle(() => onSendTyping?.(), 1500)
  ).current;

  // ⬇️ FIX: define handler used in TextField onChange
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    throttledTyping();
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Loading messages...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ borderRadius: 2, height: '600px', display: 'flex', flexDirection: 'column' }}>
      {/* Chat Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">
          Conversation with {clientName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {messages.length} messages
        </Typography>
      </Box>

      {/* Messages List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {messages.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No messages yet. Start a conversation with {clientName}.
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {messages.map((message) => (
              message.isTyping ? (
                <ListItem
                  key={message.id}
                  sx={{
                    px: 2,
                    py: 1,
                    alignItems: message.senderType === 'agent' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <Box sx={{ 
                    display : 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    flexDirection: message.senderType === 'agent' ? 'row-reverse' : 'row'
                  }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'action.hover' }}>
                      {message.senderType === 'agent' ? <SupportAgentIcon fontSize="small"/> : <PersonIcon fontSize="small"/>}
                    </Avatar>
                    <Box sx={{
                      p: 1.5,
                      px: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.text.primary, 0.1),
                      display: 'flex',
                      gap: 0.5
                    }}>
                      {[0,1,2].map(i => (
                        <Box key={i} sx={{
                          width: 6, height: 6, borderRadius: '50%',
                          bgcolor: 'text.secondary',
                          animation: 'typing-dot 1.4s infinite',
                          animationDelay: `${i*0.2}s`
                        }}/>
                      ))}
                    </Box>
                  </Box>
                </ListItem>
              ) : (
                <ListItem 
                  key={message.id} 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: message.senderType === 'agent' ? 'flex-end' : 'flex-start',
                    px: 2,
                    py: 1
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    gap: 1,
                    maxWidth: '70%',
                    flexDirection: message.senderType === 'agent' ? 'row-reverse' : 'row'
                  }}>
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32,
                        bgcolor: message.senderType === 'agent' ? 'primary.main' : 'secondary.main'
                      }}
                    >
                      {message.senderType === 'agent' ? (
                        <SupportAgentIcon fontSize="small" />
                      ) : (
                        <PersonIcon fontSize="small" />
                      )}
                    </Avatar>
                    
                    <Box sx={{ 
                      bgcolor: message.senderType === 'agent' 
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.secondary.main, 0.1),
                      borderRadius: 2,
                      p: 2,
                      border: `1px solid ${alpha(
                        message.senderType === 'agent' 
                          ? theme.palette.primary.main 
                          : theme.palette.secondary.main, 
                        0.3
                      )}`
                    }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ display: 'block', mb: 0.5 }}
                      >
                        {message.senderName} • {formatTime(message.timestamp)}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {message.message}
                      </Typography>
                      
                      {!message.isRead && message.senderType === 'client' && (
                        <Typography 
                          variant="caption" 
                          color="warning.main" 
                          sx={{ display: 'block', mt: 0.5, fontWeight: 'medium' }}
                        >
                          Unread
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </ListItem>
              )
            ))}
          </List>
        )}
        {/* scroll marker */}
        <div ref={messagesEndRef} />
      </Box>

      <Divider />

      {/* Message Input */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder={`Send a message to ${clientName}...`}
            value={newMessage}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            disabled={isSending}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || isSending}
            sx={{ px: 3 }}
            endIcon={isSending ? <CircularProgress size={16} /> : <SendIcon />}
          >
            Send
          </Button>
        </Box>
        
        {messages.some(msg => !msg.isRead && msg.senderType === 'client') && (
          <Button
            size="small"
            onClick={onMarkAsRead}
            sx={{ mt: 1 }}
          >
            Mark all as read
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default ClientChat;